/*
 * KeyFigures — "Kennzahlen im Zeitraum" below the DP chart: one row per
 * series with single values over a window (mean, min, max, quantity or
 * consumption, duty cycle, range, first/last value, count of reports,
 * coverage, reports discarded as invalid).
 *
 * The values come from GET /api/leaves/{id}/summary, computed exactly from
 * the raw points as one interval — never from what the chart shows. The
 * window follows the visible part of the chart (zoom included) unless the
 * user sets one (calendar button; pressed again, it follows the chart). Copy
 * puts the table on the clipboard (tab-separated, pastes into Excel), download
 * saves it as CSV. Icon buttons with tooltip, like the chart toolbar.
 */

import type { LeafDto } from '../../api/types';
import {
  markLabel,
  summaryCsv,
  summaryTsv,
  type LeafSummaryResponse,
  type SummaryExportRow,
} from '../../api/history';
import { showToast } from '../Toaster';
import { ToolButton, ToolGroup } from '../ui/ToolButton';
import { IconCalendar, IconCopy, IconDownload } from '../../icons/HvacIcons';
import './KeyFigures.css';

export interface KeyFigureRow {
  leaf: LeafDto;
  colour: string;
  /** `undefined` while loading. */
  summary?: LeafSummaryResponse;
  error?: string;
}

/** Window of the key figures: follows the chart, or entered exactly. */
export type KeyFigureWindow =
  | { mode: 'view' }
  | { mode: 'custom'; from: string; to: string };

interface Props {
  rows: KeyFigureRow[];
  window: KeyFigureWindow;
  /** The window actually used, for the caption. */
  effective: { from: Date; to: Date } | null;
  /** The chart is zoomed in (only relevant in mode `view`). */
  zoomed: boolean;
  busy: boolean;
  /** File name of the CSV download, without extension. */
  exportName: string;
  onWindowChange(next: KeyFigureWindow): void;
}

const NUMBER = new Intl.NumberFormat('de-AT', { maximumFractionDigits: 2 });
const SMALL = new Intl.NumberFormat('de-AT', { maximumSignificantDigits: 3 });
const DATE_TIME = new Intl.DateTimeFormat('de-AT', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

export function formatNumber(v: number): string {
  return Math.abs(v) > 0 && Math.abs(v) < 0.1 ? SMALL.format(v) : NUMBER.format(v);
}

function valueCell(v: number | undefined, unit: string | null | undefined): string {
  if (v === undefined || !Number.isFinite(v)) return '–';
  return unit ? `${formatNumber(v)} ${unit}` : formatNumber(v);
}

function percent(v: number | undefined): string {
  return v === undefined ? '–' : `${NUMBER.format(v * 100)} %`;
}

/** Mean, or duty cycle of an on/off state. */
function meanCell(s: LeafSummaryResponse): string {
  if (s.dutyCycle !== undefined) return `${percent(s.dutyCycle)} ein`;
  return valueCell(s.mean, s.unit);
}

/** Quantity of a rate, or consumption of a counter. */
function amountCell(s: LeafSummaryResponse): string {
  if (s.quantity !== undefined) return valueCell(s.quantity, s.quantityUnit);
  if (s.consumption !== undefined) return valueCell(s.consumption, s.unit);
  return '–';
}

/** `yyyy-MM-ddTHH:mm:ss` in local time for datetime-local inputs. */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const INVALID_HINT = 'Meldungen ohne gültigen Wert: vom Regler als ungültig gemeldet (Fühlerfehler, Verbindung) '
  + 'oder ausserhalb des gültigen Bereichs der Einheit bzw. der Grenzen des Datenpunkts. '
  + 'Sie zählen weder im Verlauf noch in den Kennzahlen.';

function exportRows(rows: KeyFigureRow[]): SummaryExportRow[] {
  return rows
    .filter((r): r is KeyFigureRow & { summary: LeafSummaryResponse } => r.summary !== undefined)
    .map((r) => ({ name: r.leaf.displayName || r.leaf.symbol, symbol: r.leaf.symbol, summary: r.summary }));
}

async function copyTable(rows: KeyFigureRow[]): Promise<void> {
  const text = summaryTsv(exportRows(rows));
  try {
    await navigator.clipboard.writeText(text);
    showToast({ tone: 'success', title: 'Kennzahlen kopiert', message: 'Als Tabelle einfügbar, z. B. in Excel.' });
  } catch {
    showToast({ tone: 'error', title: 'Kopieren nicht möglich', message: 'Der Browser hat den Zugriff auf die Zwischenablage verweigert. CSV verwenden.' });
  }
}

function downloadCsv(rows: KeyFigureRow[], name: string): void {
  const blob = new Blob([summaryCsv(exportRows(rows))], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function invalidCell(s: LeafSummaryResponse) {
  if (s.invalidCount === undefined) return <div role="cell">–</div>;
  return (
    <div role="cell" class={s.invalidCount > 0 ? 'hv-kpi__low' : undefined} title={s.invalidCount > 0 ? INVALID_HINT : undefined}>
      {s.invalidCount.toLocaleString('de-AT')}
    </div>
  );
}

export function KeyFigures({ rows, window, effective, zoomed, busy, exportName, onWindowChange }: Props) {
  if (rows.length === 0) return null;
  const exportable = !busy && rows.some((r) => r.summary !== undefined);

  const caption = window.mode === 'view'
    ? (zoomed ? 'sichtbarer Ausschnitt' : 'dargestellter Zeitraum')
    : 'eigener Zeitraum';

  return (
    <section class="hv-kpi" aria-label="Kennzahlen im Zeitraum">
      <header class="hv-kpi__head">
        <strong>Kennzahlen im Zeitraum</strong>
        <span class="muted">
          {caption}
          {effective && `: ${DATE_TIME.format(effective.from)} – ${DATE_TIME.format(effective.to)}`}
        </span>
        {busy && <span class="muted hv-kpi__busy">rechnet…</span>}
        <span class="hv-kpi__spacer" />
        {window.mode === 'custom' && (
          <div class="hv-kpi__custom">
            <input
              type="datetime-local"
              step={1}
              value={window.from}
              onChange={(e) => onWindowChange({ ...window, from: (e.currentTarget as HTMLInputElement).value })}
              aria-label="Von"
            />
            <span class="muted">bis</span>
            <input
              type="datetime-local"
              step={1}
              value={window.to}
              onChange={(e) => onWindowChange({ ...window, to: (e.currentTarget as HTMLInputElement).value })}
              aria-label="Bis"
            />
          </div>
        )}
        {/* Icon-Knöpfe wie in der Werkzeugleiste; der Text steht im Tooltip. */}
        <ToolGroup label="Kennzahlen">
          <ToolButton
            label={window.mode === 'view'
              ? 'Zeitraum setzen: Von und Bis sekundengenau eingeben'
              : 'Zeitraum wieder wie im Chart'}
            active={window.mode === 'custom'}
            onClick={() => {
              if (window.mode === 'custom') {
                onWindowChange({ mode: 'view' });
                return;
              }
              const from = effective?.from ?? new Date(Date.now() - 24 * 3600_000);
              const to = effective?.to ?? new Date();
              onWindowChange({ mode: 'custom', from: toLocalInput(from), to: toLocalInput(to) });
            }}
          >
            <IconCalendar size={18} />
          </ToolButton>
          <ToolButton
            label="Kopieren: Tabelle in die Zwischenablage, tabulatorgetrennt mit Dezimalkomma (für Excel)"
            disabled={!exportable}
            onClick={() => void copyTable(rows)}
          >
            <IconCopy size={18} />
          </ToolButton>
          <ToolButton
            label="CSV: Tabelle herunterladen, wie der Intervall-Export (Komma, Dezimalpunkt, Zeiten in UTC)"
            disabled={!exportable}
            onClick={() => downloadCsv(rows, exportName)}
          >
            <IconDownload size={18} />
          </ToolButton>
        </ToolGroup>
      </header>
      <div class="hv-kpi__table" role="table">
        <div class="hv-kpi__row hv-kpi__row--head" role="row">
          <div role="columnheader">Datenpunkt</div>
          <div role="columnheader" title="Zeitgewichtet über die Zeit mit gültigem Wert; bei Ein/Aus der Einschaltanteil">Mittelwert</div>
          <div role="columnheader">Min</div>
          <div role="columnheader">Max</div>
          <div role="columnheader" title="Menge (Integral einer Leistung oder eines Durchflusses) bzw. Verbrauch eines Zählers">Menge / Verbrauch</div>
          <div role="columnheader">Spannweite</div>
          <div role="columnheader">Erster</div>
          <div role="columnheader">Letzter</div>
          <div role="columnheader" title="Anzahl gültiger Meldungen des Reglers">Meldungen</div>
          <div role="columnheader" title={INVALID_HINT}>Ungültig</div>
          <div role="columnheader" title="Anteil des Zeitraums mit gültigem Wert">Abdeckung</div>
        </div>
        {rows.map((r) => {
          const s = r.summary;
          const name = r.leaf.displayName || r.leaf.symbol;
          const nameCell = (
            <div class="hv-kpi__name" role="cell" title={r.leaf.symbol}>
              <span class="hv-kpi__swatch" style={{ background: r.colour }} />
              <span class="hv-kpi__label">{name}</span>
            </div>
          );
          if (r.error) {
            return (
              <div class="hv-kpi__row" role="row" key={r.leaf.id}>
                {nameCell}
                <div class="hv-kpi__note hv-kpi__note--error" role="cell">{r.error}</div>
              </div>
            );
          }
          if (!s) {
            return (
              <div class="hv-kpi__row" role="row" key={r.leaf.id}>
                {nameCell}
                <div class="hv-kpi__note muted" role="cell">…</div>
              </div>
            );
          }
          if (s.coverage === 0 && s.count === 0) {
            const invalid = s.invalidCount ?? 0;
            return (
              <div class="hv-kpi__row" role="row" key={r.leaf.id}>
                {nameCell}
                <div class={`hv-kpi__note ${invalid > 0 ? 'hv-kpi__low' : 'muted'}`} role="cell" title={invalid > 0 ? INVALID_HINT : undefined}>
                  {invalid > 0
                    ? `Kein gültiger Wert im Zeitraum: ${invalid.toLocaleString('de-AT')} ${invalid === 1 ? 'Meldung' : 'Meldungen'} als ungültig verworfen (z. B. Fühlerfehler)`
                    : 'Keine Daten im Zeitraum'}
                  {s.lastReportAt
                    ? ` · letzte Meldung ${DATE_TIME.format(new Date(s.lastReportAt))}${s.lastValue !== undefined ? ` (${valueCell(s.lastValue, s.unit)})` : ' (ungültig)'}`
                    : ' · noch nie gemeldet'}
                </div>
              </div>
            );
          }
          const flags = (s.flags ?? []).map(markLabel);
          const lowCoverage = s.coverage < 0.9;
          return (
            <div class="hv-kpi__row" role="row" key={r.leaf.id}>
              {nameCell}
              <div role="cell">{meanCell(s)}</div>
              <div role="cell">{valueCell(s.min, s.unit)}</div>
              <div role="cell">{valueCell(s.max, s.unit)}</div>
              <div role="cell" class={flags.length > 0 ? 'hv-kpi__flagged' : undefined} title={flags.join('; ') || undefined}>
                {amountCell(s)}{flags.length > 0 && ' ⚠'}
              </div>
              <div role="cell">{valueCell(s.range, s.unit)}</div>
              <div role="cell">{valueCell(s.first, s.unit)}</div>
              <div role="cell">{valueCell(s.last, s.unit)}</div>
              <div role="cell">{s.count.toLocaleString('de-AT')}</div>
              {invalidCell(s)}
              <div
                role="cell"
                class={lowCoverage ? 'hv-kpi__low' : undefined}
                title={lowCoverage ? 'Ein Teil des Zeitraums ohne gültigen Wert; Mittelwert und Menge beziehen sich nur auf die abgedeckte Zeit' : undefined}
              >
                {percent(s.coverage)}
              </div>
            </div>
          );
        })}
      </div>
      {rows.some((r) => r.summary?.holdSource === 'assumed') && (
        <p class="hv-kpi__foot muted">
          Für Datenpunkte ohne Heartbeat ist die Haltedauer angenommen; die Abdeckung ist dort unsicher.
        </p>
      )}
    </section>
  );
}
